import React, { useMemo, useState } from "react";
import { Archive, Eye } from "lucide-react";
import { toast } from "sonner";
import { SaddleCloth } from "./SaddleCloth";
import { RaceTimeInfo } from "./NextRaceLineup";
import { raceGlance } from "../lib/raceGlance";
import { buildExoticGroups } from "../lib/exoticsHelpers";
import { saveGlance } from "../lib/glanceApi";
import { todayLocalIso } from "../lib/dateHelpers";

const monoStyle = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };

const fmtDate = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  return y && m && d
    ? new Date(y, m - 1, d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })
    : String(iso);
};

const Horse = ({ h, testId, note }) => (
  <div className="flex items-center gap-2" data-testid={testId}>
    <SaddleCloth n={h.post} />
    <span className="text-sm text-[#f5ecd6] tabular-nums">{h.oddsText || "—"}</span>
    {h.isMl && <span className="text-[9px] text-stone-500">ML</span>}
    {note && <span className="text-[9px] uppercase tracking-[0.14em] text-stone-500">{note}</span>}
  </div>
);

const Block = ({ title, testId, children }) => (
  <div className="flex-1 min-w-[200px] px-5 py-4" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-[0.22em] text-[#84cc16] font-bold mb-3">{title}</div>
    <div className="flex flex-wrap gap-3">{children}</div>
  </div>
);

const Empty = ({ testId, children }) => (
  <span className="text-xs text-stone-500" data-testid={testId}>{children}</span>
);

const IqRow = ({ order }) => (
  <div className="px-5 py-3 border-t border-stone-800" data-testid="glance-iq-row">
    <div className="text-[11px] uppercase tracking-[0.22em] text-[#84cc16] font-bold mb-2">IQ Power Order</div>
    <div className="flex flex-wrap gap-2">
      {order.map((p, i) => (
        <div key={p} className="flex flex-col items-center gap-0.5" data-testid={`glance-iq-${i + 1}`}>
          <span className="text-[9px] text-stone-500">#{i + 1}</span>
          <SaddleCloth n={p} />
        </div>
      ))}
      {!order.length && <Empty testId="glance-iq-empty">No ranks yet</Empty>}
    </div>
  </div>
);

export const RaceGlance = ({ leftRows, leftElims, keyPost, track, trackSlug, race, date, postTime, mtp, owner }) => {
  const [saving, setSaving] = useState(false);
  const g = useMemo(() => raceGlance(leftRows), [leftRows]);
  const key = useMemo(
    () => buildExoticGroups(leftRows, leftElims, 3, keyPost).triKey,
    [leftRows, leftElims, keyPost]
  );
  const iqOrder = useMemo(
    () =>
      leftRows
        .filter((r) => r.RANK !== "" && r.RANK != null && r.S !== "")
        .sort((a, b) => Number(a.RANK) - Number(b.RANK))
        .map((r) => Number(r.S)),
    [leftRows]
  );
  const keyHorse = key != null ? g.byPost.get(key) : null;
  const day = date || todayLocalIso();

  const archive = async () => {
    setSaving(true);
    try {
      await saveGlance({
        track: track || "",
        track_slug: trackSlug,
        date: day,
        race: Number(race),
        strong: g.strong.map((h) => h.post),
        key: key ?? null,
        longshots: g.longShots.map((h) => h.post),
        iq_order: iqOrder,
      });
      toast.success(`Race at a Glance archived · ${track} R${race}`);
    } catch (e) {
      toast.error(`Could not archive: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-10 pt-4" data-testid="race-glance">
      <div className="rounded-lg border border-stone-800 bg-[#0e0e0e] overflow-hidden" style={monoStyle}>
        <div className="flex items-center flex-wrap gap-x-4 gap-y-2 px-5 py-3 border-b border-stone-800">
          <div className="flex items-center gap-2 text-[#f5ecd6]">
            <Eye size={16} className="text-[#84cc16]" />
            <span className="text-sm uppercase tracking-[0.2em]">Race at a Glance</span>
          </div>
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#f5ecd6]" data-testid="glance-date">
            {fmtDate(day)}
          </span>
          <RaceTimeInfo track={track} race={race} postTime={postTime} mtp={mtp} />
          {owner && (
            <button
              type="button"
              onClick={archive}
              disabled={saving || !trackSlug}
              data-testid="glance-archive-btn"
              className="ml-auto inline-flex items-center gap-2 px-3 h-8 rounded border border-stone-600 hover:border-[#84cc16] hover:text-[#84cc16] text-stone-200 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors disabled:opacity-50"
            >
              <Archive size={14} /> {saving ? "Saving…" : "Archive"}
            </button>
          )}
        </div>
        <div className="flex flex-wrap divide-y sm:divide-y-0 sm:divide-x divide-stone-800">
          <Block title="Strong Contenders" testId="glance-strong">
            {g.strong.length ? (
              g.strong.map((h) => (
                <Horse key={h.post} h={h} testId={`glance-strong-${h.post}`} note={h.filler ? "over 3/1" : null} />
              ))
            ) : (
              <Empty testId="glance-strong-empty">No priced horses yet</Empty>
            )}
          </Block>
          <Block title="The Key Horse" testId="glance-key">
            {keyHorse ? (
              <Horse h={keyHorse} testId={`glance-key-${keyHorse.post}`} note={keyPost != null ? "locked" : null} />
            ) : (
              <Empty testId="glance-key-empty">No key yet</Empty>
            )}
          </Block>
          <Block title="Long Shot Play" testId="glance-longshot">
            {g.longShots.map((h) => (
              <Horse key={h.post} h={h} testId={`glance-longshot-${h.post}`} />
            ))}
          </Block>
        </div>
        <IqRow order={iqOrder} />
      </div>
    </section>
  );
};
